import { forwardRef, useRef } from 'react';
import { TextInput, View } from 'react-native';
import { BaseTextInputProps } from './BaseTextInput.types';
import { useFocus } from 'src/hooks/useFocus';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';
import { useFocusContext } from 'src/contexts/FocusContext';
import { DEFAULT_FOCUS_RING } from 'src/theme-v2/focus';

export const BaseTextInput = forwardRef(
  (
    {
      children,
      style,
      inputStyle,
      accessibilityRole,
      disabledStyle,
      focusId,
      focusOrder,
      focusGroup,
      disabled,
      focusStyle,
      focusable = true,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      onKeyPress: onKeyPressProp,
      accessible = true,
      ...textInputProps
    }: BaseTextInputProps,
    ref,
  ) => {
    const textInputRef = ref ?? useRef(null);

    const isFocusable = !disabled && focusable;

    const { focusCloseFocusableElement } = useFocusContext();

    const { blur, focused } = useFocus({
      id: focusId,
      order: focusOrder,
      group: focusGroup,
      focusable: isFocusable,
      ref: textInputRef,
    });

    let textInputStyle;
    if (disabled) {
      textInputStyle = disabledStyle;
    } else if (focused) {
      const flatFocusStyle = Array.isArray(focusStyle) ? Object.assign({}, ...focusStyle.filter(Boolean)) : focusStyle;
      const hasFocusIndicator =
        flatFocusStyle != null &&
        ('borderColor' in flatFocusStyle || 'backgroundColor' in flatFocusStyle || 'boxShadow' in flatFocusStyle);
      textInputStyle = mergeStyles(hasFocusIndicator ? undefined : DEFAULT_FOCUS_RING, focusStyle);
    }

    return (
      <View style={mergeStyles(style, textInputStyle)}>
        <TextInput
          style={inputStyle}
          accessible={accessible}
          accessibilityState={{ disabled: !!disabled }}
          accessibilityRole={accessibilityRole}
          tabIndex={isFocusable ? 0 : -1}
          ref={textInputRef}
          onFocus={(e: any) => {
            if (onFocusProp) onFocusProp(e);
          }}
          onBlur={(e: any) => {
            blur();
            if (onBlurProp) onBlurProp(e);
          }}
          onKeyPress={(e: any) => {
            if (e.nativeEvent.key === 'Tab') {
              e.preventDefault();
              e.stopPropagation();
              focusCloseFocusableElement(!e.nativeEvent.shiftKey);
            }
            if (onKeyPressProp) onKeyPressProp(e);
          }}
          {...textInputProps}
        />
        {children?.({ focused, disabled: !!disabled })}
      </View>
    );
  },
);

BaseTextInput.displayName = 'BaseTextInput';
